import type { TPlayerDiagnosticsSnapshot } from '@lucimark/shared/models/player-diagnostics'
import {
  playerDiagnosticsSnapshotSchema

} from '@lucimark/shared/models/player-diagnostics'
import z from 'zod'

export const PLAYER_DIAGNOSTIC_COMMAND_MAX_TTL_SECONDS = 60 as const
export const PLAYER_DIAGNOSTIC_SESSION_MAX_TTL_SECONDS = 3_600 as const
export const PLAYER_DIAGNOSTIC_COMMAND_RESULT_TTL_MS = 60_000 as const
export const PLAYER_DIAGNOSTIC_COMMAND_DEFAULT_TTL_SECONDS = 30 as const

/** Operator command route budget, per user per tenant, per 60s window. */
export const PLAYER_DIAGNOSTIC_COMMAND_OPERATOR_MAX_PER_MINUTE = 30 as const
/** Operator command route budget, per (user, player), per 60s window. */
export const PLAYER_DIAGNOSTIC_COMMAND_PER_PLAYER_MAX_PER_MINUTE = 15 as const
/** Player result route budget, per player, per 60s window. */
export const PLAYER_DIAGNOSTIC_RESULT_PLAYER_MAX_PER_MINUTE = 60 as const

export const PLAYER_DIAGNOSTIC_COMMAND_TYPES = [
  'getDiagnosticsSnapshot',
  'clearDiagnosticEvents',
  'enableDiagnosticSession'
] as const

export const playerDiagnosticCommandTypeSchema = z.enum(PLAYER_DIAGNOSTIC_COMMAND_TYPES)
export type TPlayerDiagnosticCommandType = z.infer<typeof playerDiagnosticCommandTypeSchema>

const baseCommand = z.object({
  commandId: z.string().uuid(),
  issuedAt: z.number().int().positive(),
  ttlSeconds: z.number().int().min(1).max(PLAYER_DIAGNOSTIC_COMMAND_MAX_TTL_SECONDS)
})

export const playerDiagnosticCommandSchema = z.discriminatedUnion('type', [
  baseCommand.extend({ type: z.literal('getDiagnosticsSnapshot') }).strict(),
  baseCommand.extend({ type: z.literal('clearDiagnosticEvents') }).strict(),
  baseCommand.extend({
    type: z.literal('enableDiagnosticSession'),
    sessionTtlSeconds: z.number().int().min(60).max(PLAYER_DIAGNOSTIC_SESSION_MAX_TTL_SECONDS)
  }).strict()
]).superRefine((cmd, ctx) => {
  if (cmd.type === 'enableDiagnosticSession' && cmd.ttlSeconds > cmd.sessionTtlSeconds) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['ttlSeconds'],
      message: 'ttlSeconds must not exceed sessionTtlSeconds for enableDiagnosticSession'
    })
  }
})

export type TPlayerDiagnosticCommand = z.infer<typeof playerDiagnosticCommandSchema>

export const playerDiagnosticCommandBodySchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('getDiagnosticsSnapshot') }).strict(),
  z.object({ type: z.literal('clearDiagnosticEvents') }).strict(),
  z.object({
    type: z.literal('enableDiagnosticSession'),
    sessionTtlSeconds: z.number().int().min(60).max(PLAYER_DIAGNOSTIC_SESSION_MAX_TTL_SECONDS).optional()
  }).strict()
])

export type TPlayerDiagnosticsCommandBody = z.infer<typeof playerDiagnosticCommandBodySchema>

export const playerDiagnosticCommandResultSchema = z.object({
  commandId: z.string().uuid(),
  ok: z.boolean(),
  error: z.string().max(512).optional(),
  snapshot: playerDiagnosticsSnapshotSchema.optional(),
  sessionActiveUntil: z.number().int().positive().optional()
}).strict().superRefine((r, ctx) => {
  if (r.ok === false && (r.error == null || r.error === '')) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['error'],
      message: 'error is required when ok is false'
    })
  }
})

export type TPlayerDiagnosticCommandResult = z.infer<typeof playerDiagnosticCommandResultSchema>

export const playerDiagnosticRateLimitBodySchema = z.object({
  error: z.literal('rate_limited'),
  retryAfterSeconds: z.number().int().positive(),
  scope: z.enum(['user', 'player'])
}).strict()

export type TPlayerDiagnosticRateLimitBody = z.infer<typeof playerDiagnosticRateLimitBodySchema>

export function parsePlayerDiagnosticRateLimitBody(raw: unknown): TPlayerDiagnosticRateLimitBody | null {
  const parsed = playerDiagnosticRateLimitBodySchema.safeParse(raw)
  return parsed.success ? parsed.data : null
}

export interface TPlayerDiagnosticsCommandResponse {
  commandId: string
  delivered: boolean
}

export const playerDiagnosticResultWsMessageSchema = z.object({
  kind: z.literal('player-diagnostic-result'),
  playerId: z.string().min(1),
  result: playerDiagnosticCommandResultSchema
}).strict()

export type TPlayerDiagnosticResultWsMessage = z.infer<typeof playerDiagnosticResultWsMessageSchema>

export type { TPlayerDiagnosticsSnapshot }
